(function () {
  'use strict';

  const selectors = {
    slider: '[data-slider]',
    item: '[data-slide]',
    lazyImage: '.lazy-image',
    scrollDown: '[data-scroll-down]',
    siteHeader: '[data-site-header]',
  };

  const classes = {
    headerFixed: 'site-header--fixed',
    sliderNoCachedImages: 'slider--no-cached-images',
    sliderImgLoaded: 'slider--img-loaded',
    imgIn: 'img-in',
  };

  const instances = {};

  class USSlideshow {
    constructor(sectionEl) {
      this.section = sectionEl;
      this.container = sectionEl.querySelector(selectors.slider);
      if (!this.container) return;

      this.header = document.querySelector(selectors.siteHeader);
      this.flkty = null;
      this.resizeEvent = () => { if (this.flkty) this.flkty.resize(); };

      this.init();
    }

    init() {
      const Flickity = window.theme && window.theme.Flickity;
      if (!Flickity) return;

      const slides = this.container.querySelectorAll(selectors.item);
      if (slides.length < 2) return;

      const duration = parseInt(this.container.dataset.duration) || 4000;
      const pageDots = this.container.dataset.pageDots === 'true';
      const navArrows = this.container.dataset.navArrows === 'true';
      const autoplay = this.container.dataset.autoplay === 'true' ? duration : false;

      this.flkty = new Flickity(this.container, {
        fade: true,
        cellSelector: selectors.item,
        autoPlay: autoplay,
        wrapAround: true,
        adaptiveHeight: true,
        setGallerySize: true,
        imagesLoaded: true,
        pageDots: pageDots,
        prevNextButtons: navArrows,
        on: {
          ready: () => {
            // img-in class is normally added by the TR theme's loadedImagesEventHook
            // but only fires for images with is-loading class (lazy-image snippet).
            // Our section uses image-fill snippet which does NOT add is-loading.
            // Without img-in, the slider--custom CSS animation never triggers → flash.
            // We add it manually after Flickity is ready.
            requestAnimationFrame(() => {
              this.container.classList.add('img-in');
            });
            requestAnimationFrame(this.resizeEvent);
            document.addEventListener('theme:vars', this.resizeEvent);
            this.handleFirstSlideAnimation(true);
          },
          change: () => {
            this.handleFirstSlideAnimation(false);
          },
        },
      });

      if (window.theme.flickitySmoothScrolling) {
        window.theme.flickitySmoothScrolling(this.container);
      }

      this.bindScrollButton();

      // Custom transition drag animation (only when transition style = "custom")
      if (this.container.classList.contains('slider--custom')) {
        this.setupCustomTransition();
      }
    }

    handleFirstSlideAnimation(onLoad) {
      if (!onLoad) {
        this.container.classList.remove(classes.sliderNoCachedImages, classes.sliderImgLoaded);
        return;
      }

      const firstSlide = this.container.querySelector(selectors.item);
      if (!firstSlide) return;

      const lazyImages = firstSlide.querySelectorAll(selectors.lazyImage);
      if (!lazyImages.length || this.container.classList.contains(classes.imgIn)) return;

      this.container.classList.add(classes.sliderNoCachedImages);

      const onTransitionEnd = () => {
        requestAnimationFrame(() => this.container.classList.add(classes.sliderImgLoaded));
        lazyImages[0].removeEventListener('transitionend', onTransitionEnd);
      };
      lazyImages[0].addEventListener('transitionend', onTransitionEnd);
    }

    bindScrollButton() {
      const btn = this.container.querySelector(selectors.scrollDown);
      if (!btn) return;

      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const headerHeight = this.header && this.header.classList.contains(classes.headerFixed) ? 60 : 0;
        const top = Math.ceil(this.container.offsetTop + this.container.offsetHeight - headerHeight);
        window.scrollTo({ top, left: 0, behavior: 'smooth' });
      });
    }

    // Custom transition: overlay slides left on drag (desktop only)
    // Handles what custom-slide-transition.liquid does, directly on our Flickity instance
    setupCustomTransition() {
      const flkty = this.flkty;
      const container = this.container;
      const maxSlide = 80;
      const isDesktop = () => window.innerWidth >= 768;

      const getActiveOverlay = () => {
        const selected = flkty.selectedElement;
        return selected ? selected.querySelector('.slide__text__overlay') : null;
      };

      const resetOverlays = () => {
        container.querySelectorAll('.slide__text__overlay').forEach((el) => {
          el.classList.remove('is-dragging');
          el.style.transform = '';
        });
      };

      flkty.on('dragStart', () => {
        if (!isDesktop()) return;
        const overlay = getActiveOverlay();
        if (overlay) overlay.classList.add('is-dragging');
      });

      flkty.on('dragMove', (event, pointer, moveVector) => {
        if (!isDesktop()) return;
        const overlay = getActiveOverlay();
        if (!overlay) return;
        if (moveVector.x < 0) {
          const progress = Math.min(Math.abs(moveVector.x) / 200, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          overlay.style.transform = `translateX(${-eased * maxSlide}px)`;
        } else {
          overlay.style.transform = '';
        }
      });

      flkty.on('dragEnd', resetOverlays);
      flkty.on('settle', resetOverlays);
      flkty.on('change', resetOverlays);

      window.addEventListener('resize', () => {
        if (!isDesktop()) resetOverlays();
      });
    }

    onBlockSelect(evt) {
      if (!this.flkty) return;
      const index = [...evt.target.parentNode.children].indexOf(evt.target);
      this.flkty.select(index);
      this.flkty.pausePlayer();
    }

    onBlockDeselect() {
      if (this.flkty && this.container.dataset.autoplay === 'true') {
        this.flkty.playPlayer();
      }
    }

    onReorder() {
      this.handleFirstSlideAnimation(false);
      if (this.flkty) this.flkty.resize();
    }

    onUnload() {
      this.handleFirstSlideAnimation(false);
      if (this.flkty) {
        document.removeEventListener('theme:vars', this.resizeEvent);
        this.flkty.destroy();
        this.flkty = null;
      }
    }
  }

  function getSectionId(sectionEl) {
    const slider = sectionEl.querySelector(selectors.slider);
    return slider ? slider.dataset.sectionId : null;
  }

  function initSection(sectionEl) {
    const id = getSectionId(sectionEl);
    if (!id) return;
    instances[id] = new USSlideshow(sectionEl);
  }

  function initAll() {
    document.querySelectorAll('.us-slideshow').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.classList.contains('us-slideshow')) initSection(e.target);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const id = getSectionId(e.target);
    if (id && instances[id]) {
      instances[id].onUnload();
      delete instances[id];
    }
  });

  document.addEventListener('shopify:section:reorder', (e) => {
    const id = getSectionId(e.target);
    if (id && instances[id]) instances[id].onReorder();
  });

  document.addEventListener('shopify:block:select', (e) => {
    const section = e.target.closest('.us-slideshow');
    if (!section) return;
    const id = getSectionId(section);
    if (id && instances[id]) instances[id].onBlockSelect(e);
  });

  document.addEventListener('shopify:block:deselect', (e) => {
    const section = e.target.closest('.us-slideshow');
    if (!section) return;
    const id = getSectionId(section);
    if (id && instances[id]) instances[id].onBlockDeselect();
  });
})();
